import { useEffect, useRef, useState } from "react";
import { Bug, Camera, Loader2, Mic, MicOff, Upload, X, ListChecks } from "lucide-react";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Link } from "react-router-dom";

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl);
  return res.blob();
};

export const BugReportButton = () => {
  const { isAdmin } = useIsAdmin();
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [openCount, setOpenCount] = useState(0);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Load open bug count for admins, with realtime updates
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setAuthed(!!s?.user));
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      const { count } = await supabase
        .from("bug_reports")
        .select("id", { count: "exact", head: true })
        .eq("status", "open");
      if (!cancelled) setOpenCount(count || 0);
    };
    load();
    const channel = supabase
      .channel("bug_reports_badge")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bug_reports" },
        () => load()
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const toggleDictation = () => {
    const SR: any =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    let baseline = description ? description + " " : "";
    rec.onresult = (e: any) => {
      let finalText = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      if (finalText) baseline += finalText;
      setDescription((baseline + interim).trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e: any) => {
      console.error("Speech error:", e);
      toast.error("Voice input error: " + (e.error || "unknown"));
      setListening(false);
    };
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      // brief delay so the dialog/button can hide before capture
      await new Promise((r) => setTimeout(r, 150));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        scale: Math.min(window.devicePixelRatio || 1, 2),
        ignoreElements: (el) => el.hasAttribute("data-bug-report-ignore"),
      });
      setScreenshot(canvas.toDataURL("image/png"));
    } catch (e) {
      console.error("Screenshot failed:", e);
      toast.error("Couldn't auto-capture the screen. You can upload one manually.");
    } finally {
      setCapturing(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setScreenshot(null);
    setTitle("");
    setDescription("");
    setEmail("");
    // capture after the dialog is open (it's hidden via data attr)
    await captureScreenshot();
  };

  // Stop dictation when dialog closes
  useEffect(() => {
    if (!open && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
  }, [open]);

  const handleFileUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => setScreenshot(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Please add a short title.");
      return;
    }
    setSubmitting(true);
    try {
      let screenshotUrl: string | null = null;

      if (screenshot) {
        const blob = await dataUrlToBlob(screenshot);
        const path = `${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.png`;
        const { error: uploadErr } = await supabase.storage
          .from("bug-reports")
          .upload(path, blob, { contentType: "image/png", upsert: false });
        if (uploadErr) throw uploadErr;
        screenshotUrl = path;
      }

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      const { error: insertErr } = await supabase.from("bug_reports").insert({
        title: title.trim(),
        description: description.trim() || null,
        screenshot_url: screenshotUrl,
        page_url: window.location.href,
        browser_info: {
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          user_agent: navigator.userAgent,
          language: navigator.language,
        },
        reporter_user_id: user?.id ?? null,
        reporter_email: user?.email ?? (email.trim() || null),
        status: "open",
        app: null, // nomad-fest scope — keeps notifications isolated per project
      });
      if (insertErr) throw insertErr;

      toast.success("Thanks! Your bug report has been sent.");
      setOpen(false);
    } catch (e: any) {
      console.error("Bug report submit failed:", e);
      toast.error(e?.message || "Couldn't send your bug report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        data-bug-report-ignore
        className="fixed bottom-4 left-4 z-[60] print:hidden flex items-center gap-2"
      >
        {isAdmin && (
          <Button
            asChild
            size="sm"
            variant="outline"
            className="shadow-md gap-2 rounded-full bg-background h-9 px-3 opacity-80 hover:opacity-100"
          >
            <Link to="/admin/bug-reports">
              <ListChecks className="h-4 w-4" />
              <span className="hidden md:inline">View reports</span>
            </Link>
          </Button>
        )}
        {!isAdmin && authed && (
          <Button
            asChild
            size="sm"
            variant="outline"
            className="shadow-md gap-2 rounded-full bg-background h-9 px-3 opacity-80 hover:opacity-100"
          >
            <Link to="/my-bug-reports">
              <ListChecks className="h-4 w-4" />
              <span className="hidden md:inline">My reports</span>
            </Link>
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={handleOpen}
          className="shadow-md gap-2 rounded-full relative h-9 px-3 opacity-80 hover:opacity-100"
        >
          <Bug className="h-4 w-4" />
          <span className="hidden md:inline">Report a bug</span>
          {isAdmin && openCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold flex items-center justify-center shadow ring-2 ring-background animate-pulse">
              {openCount > 99 ? "99+" : openCount}
            </span>
          )}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-bug-report-ignore
          className="max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Report a bug</DialogTitle>
            <DialogDescription>
              Tell us what went wrong. We'll automatically capture a screenshot of this page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Screenshot</Label>
              <div className="border rounded-md bg-muted/30 p-2 flex flex-col items-center gap-2">
                {capturing ? (
                  <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Capturing screen…
                  </div>
                ) : screenshot ? (
                  <div className="relative w-full">
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      className="w-full max-h-48 object-contain rounded"
                    />
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute top-1 right-1 h-6 w-6"
                      onClick={() => setScreenshot(null)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-6">No screenshot yet</div>
                )}
                <div className="flex gap-2 w-full">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={captureScreenshot}
                    disabled={capturing}
                  >
                    <Camera className="h-4 w-4" />
                    {screenshot ? "Recapture" : "Capture screen"}
                  </Button>
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileUpload(f);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                      asChild
                    >
                      <span>
                        <Upload className="h-4 w-4" />
                        Upload image
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-title">Title *</Label>
              <Input
                id="bug-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary, e.g. 'Save button doesn't work'"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="bug-desc">What happened?</Label>
                <Button
                  type="button"
                  variant={listening ? "destructive" : "outline"}
                  size="sm"
                  className="h-7 gap-1.5"
                  onClick={toggleDictation}
                >
                  {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                  {listening ? "Stop" : "Dictate"}
                </Button>
              </div>
              <Textarea
                id="bug-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Steps to reproduce, what you expected, what you saw… or tap Dictate to speak."
                rows={4}
              />
              {listening && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-destructive animate-pulse" />
                  Listening… speak now.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-email">Your email (optional)</Label>
              <Input
                id="bug-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="So we can follow up if needed"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || capturing}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportButton;